import { create } from "zustand";

export type ChoroplethMode = "raw" | "adjusted";
export type Theme = "light" | "dark";
// "street"          — OSM / CartoDB (theme-aware)
// "satellite"       — Esri World Imagery, latest available (no time)
// "satellite-time"  — NASA GIBS MODIS, daily by activeTick (lower-res)
export type Basemap = "street" | "satellite" | "satellite-time";

const THEME_STORAGE_KEY = "firefair.headerTheme";
const BASEMAP_STORAGE_KEY = "firefair.basemap";
const DRAWER_WIDTH_STORAGE_KEY = "firefair.drawerWidth";
export const DRAWER_WIDTH_DEFAULT = 420;
export const DRAWER_WIDTH_MIN = 320;
export const DRAWER_WIDTH_MAX = 760;

function loadInitialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage?.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function loadInitialBasemap(): Basemap {
  if (typeof window === "undefined") return "street";
  const stored = window.localStorage?.getItem(BASEMAP_STORAGE_KEY);
  if (stored === "satellite" || stored === "satellite-time") return stored;
  return "street";
}

function loadInitialDrawerWidth(): number {
  if (typeof window === "undefined") return DRAWER_WIDTH_DEFAULT;
  const raw = window.localStorage?.getItem(DRAWER_WIDTH_STORAGE_KEY);
  const n = raw ? parseInt(raw, 10) : NaN;
  if (!Number.isFinite(n)) return DRAWER_WIDTH_DEFAULT;
  return Math.min(DRAWER_WIDTH_MAX, Math.max(DRAWER_WIDTH_MIN, n));
}

interface UIState {
  activeTick:      string | null;
  selectedCellId:  string | null;
  // v3.1: choropleth toggle (raw p_hat ↔ adjusted p_tilde)
  choroplethMode:  ChoroplethMode;
  // v3.1: county-level drill-down
  drillCounty:     string | null;
  drawerOpen:      boolean;
  // v3.1 chat state
  chatSessionId:   string | null;
  // header light/dark theme; persisted in localStorage
  theme:           Theme;
  // street vs satellite basemap; persisted in localStorage
  basemap:         Basemap;
  // Toggle the risk overlays (cell points, county/tract choropleth) so the
  // user can inspect the underlying basemap (esp. satellite) unobstructed.
  riskOverlayVisible: boolean;
  // resizable cell drawer width in px; persisted in localStorage
  drawerWidth:     number;

  setTick:           (tick: string) => void;
  selectCell:        (cellId: string | null) => void;
  setChoroplethMode: (mode: ChoroplethMode) => void;
  setDrillCounty:    (countyGeoid: string | null) => void;
  setChatSessionId:  (sessionId: string | null) => void;
  setTheme:          (theme: Theme) => void;
  toggleTheme:       () => void;
  setBasemap:        (basemap: Basemap) => void;
  toggleBasemap:     () => void;
  toggleRiskOverlay: () => void;
  setDrawerWidth:    (width: number) => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  activeTick:     null,
  selectedCellId: null,
  choroplethMode: "adjusted",
  drillCounty:    null,
  drawerOpen:     false,
  chatSessionId:  null,
  theme:          loadInitialTheme(),
  basemap:        loadInitialBasemap(),
  riskOverlayVisible: true,
  drawerWidth:    loadInitialDrawerWidth(),

  setTick:           (tick)        => set({ activeTick: tick }),
  selectCell:        (cellId)      => set({
    selectedCellId: cellId,
    drawerOpen: cellId !== null,
    chatSessionId: null,  // reset session per cell
  }),
  setChoroplethMode: (mode)        => set({ choroplethMode: mode }),
  setDrillCounty:    (countyGeoid) => set({ drillCounty: countyGeoid }),
  setChatSessionId:  (sessionId)   => set({ chatSessionId: sessionId }),
  setTheme:          (theme)       => {
    try { window.localStorage?.setItem(THEME_STORAGE_KEY, theme); } catch { /* ignore */ }
    set({ theme });
  },
  toggleTheme:       () => {
    const next: Theme = get().theme === "dark" ? "light" : "dark";
    try { window.localStorage?.setItem(THEME_STORAGE_KEY, next); } catch { /* ignore */ }
    set({ theme: next });
  },
  setBasemap:        (basemap)     => {
    try { window.localStorage?.setItem(BASEMAP_STORAGE_KEY, basemap); } catch { /* ignore */ }
    set({ basemap });
  },
  toggleBasemap:     () => {
    // street → satellite (Esri, live) → satellite-time (GIBS, by tick) → street
    const order: Basemap[] = ["street", "satellite", "satellite-time"];
    const cur = get().basemap;
    const next = order[(order.indexOf(cur) + 1) % order.length];
    try { window.localStorage?.setItem(BASEMAP_STORAGE_KEY, next); } catch { /* ignore */ }
    set({ basemap: next });
  },
  toggleRiskOverlay: () => set({ riskOverlayVisible: !get().riskOverlayVisible }),
  setDrawerWidth:    (width)       => {
    const clamped = Math.min(DRAWER_WIDTH_MAX, Math.max(DRAWER_WIDTH_MIN, Math.round(width)));
    try { window.localStorage?.setItem(DRAWER_WIDTH_STORAGE_KEY, String(clamped)); } catch { /* ignore */ }
    set({ drawerWidth: clamped });
  },
}));

// Sync URL search params ↔ store on mount
export function syncFromUrl(): void {
  const p = new URLSearchParams(window.location.search);
  const tick = p.get("tick");
  const cell = p.get("cell");
  const mode = p.get("mode");
  const countyDrill = p.get("county");
  useUIStore.setState({
    activeTick:     tick ?? null,
    selectedCellId: cell ?? null,
    drawerOpen:     cell !== null,
    choroplethMode: mode === "raw" ? "raw" : "adjusted",
    drillCounty:    countyDrill ?? null,
  });
}

export function pushUrl(state: Partial<UIState>): void {
  const { activeTick, selectedCellId, choroplethMode, drillCounty } = {
    ...useUIStore.getState(),
    ...state,
  };
  const p = new URLSearchParams();
  if (activeTick) p.set("tick", activeTick);
  if (selectedCellId) p.set("cell", selectedCellId);
  if (choroplethMode === "raw") p.set("mode", "raw");
  if (drillCounty) p.set("county", drillCounty);
  const qs = p.toString();
  window.history.replaceState(null, "", qs ? `?${qs}` : window.location.pathname);
}
